import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useBboxOverlay } from "@/lib/useBboxOverlay";
import { toast } from "sonner";

import { CommentBody } from "@/components/CommentBody";
import { Lightbox } from "@/components/Lightbox";
import { api, type MaterialFamily, type MaterialSubtype, type ColorFamily, type ConditionGrade, type PhotoKind, type ProductComment } from "@/lib/api";
import { READONLY, LS_USERNAME_KEY } from "@/lib/config";
import { formatDate, formatMoney } from "@/lib/format";
import { ArrowLeft, Edit, Link as LinkIcon, MessageDots, Spinner } from "@/lib/icons";

function PhotoCard({
  ph,
  single,
  onLightbox,
}: {
  ph: import("@/lib/api").PhotoRow;
  single: boolean;
  onLightbox: (bbox?: string | null) => void;
}) {
  const { imgRef, overlayStyle } = useBboxOverlay(ph.bbox);
  return (
    <figure className="overflow-hidden rounded-md border border-zinc-200 bg-zinc-50">
      <button
        type="button"
        onClick={() => onLightbox(ph.bbox)}
        aria-label="view full-size"
        className="relative block w-full"
      >
        <img
          ref={imgRef}
          src={api.photoUrl(ph.id)}
          alt=""
          loading="lazy"
          className={`w-full bg-zinc-50 object-contain transition-transform duration-150 hover:scale-[1.01] ${single ? "max-h-[480px]" : "h-64"}`}
        />
        {overlayStyle && (
          <div
            className="pointer-events-none absolute border-2 border-red-500"
            style={{
              ...overlayStyle,
              boxShadow: "0 0 0 2px rgba(255,255,255,0.55)",
            }}
          />
        )}
      </button>
      <figcaption className="border-t border-zinc-100 bg-white p-2 text-xs text-zinc-500">
        <div className="flex items-center justify-between gap-2">
          <span>
            {ph.is_primary ? "primary · " : ""}
            {ph.sharpness ? `sharp ${Math.round(ph.sharpness)}` : "—"}
          </span>
          <PhotoKindSelect photoId={ph.id} value={ph.photo_kind} />
        </div>
        {ph.barcode_value && (
          <div className="mt-1 font-mono text-xs text-indigo-700">
            {ph.barcode_type}: {ph.barcode_value}
          </div>
        )}
      </figcaption>
    </figure>
  );
}

export function ProductDetail() {
  const { uid = "" } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [lightbox, setLightbox] = useState<{ src: string; bbox?: string | null } | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["product", uid],
    queryFn: () => api.product(uid),
    enabled: !!uid,
  });

  const patch = useMutation({
    mutationFn: (fields: Parameters<typeof api.patchProduct>[1]) =>
      api.patchProduct(uid, fields),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["product", uid] }),
    onError: () => toast.error("Couldn't save your changes. Please try again."),
  });

  const archive = useMutation({
    mutationFn: () => api.archive(uid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["products"] });
      navigate("/");
    },
    onError: () => toast.error("Couldn't archive this product. Please try again."),
  });

  const restore = useMutation({
    mutationFn: () => api.restore(uid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["product", uid] });
      qc.invalidateQueries({ queryKey: ["products"] });
    },
    onError: () => toast.error("Couldn't restore this product. Please try again."),
  });

  if (isLoading || !data) return <p className="text-sm text-zinc-500">loading…</p>;
  const { product, photos, links, duplicates, parent, children } = data;

  const p = product;

  return (
    <div className="space-y-6">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-800">
        <ArrowLeft className="size-4" /> back to products
      </Link>

      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{p.name}</h1>
          <p className="mt-1 text-sm text-zinc-500">
            {p.batch} · {p.group_key} · processed {formatDate(p.processed_at)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
            p.status === "done" ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
            : p.status === "needs_review" ? "bg-amber-50 text-amber-700 ring-amber-200"
            : "bg-rose-50 text-rose-700 ring-rose-200"
          }`}>{p.status}</span>
          {!READONLY && !p.archived_at && (
            <button
              type="button"
              onClick={() => archive.mutate()}
              className="rounded-md border border-zinc-200 px-2 py-1 text-xs text-zinc-500 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700"
            >
              Archive
            </button>
          )}
        </div>
      </header>

      <p className="text-sm text-zinc-500">
        {READONLY
          ? "Full record for this product. Pricing links are sourced from manufacturer pages and Australian retailers."
          : "Full record for this product. All fields are editable inline — click any field and type to update. Pricing links are sourced from manufacturer pages and Australian retailers."}
      </p>

      {p.archived_at && (
        <div className="flex items-center justify-between gap-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span>
            Archived {formatDate(p.archived_at)} — this product won't appear in the main list.
          </span>
          {!READONLY && (
            <button
              type="button"
              onClick={() => restore.mutate()}
              className="shrink-0 rounded-md bg-amber-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-800"
            >
              Restore
            </button>
          )}
        </div>
      )}

      {duplicates.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {duplicates.length} possible duplicate{duplicates.length === 1 ? "" : "s"}.{" "}
          <Link to="/duplicates" className="font-medium underline">review</Link>
        </div>
      )}

      {/* Responsive section grid — 1 col → 2 col (md) → 3 col (lg) */}
      <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-3">

        {/* 1. Links + parent/children — 1 col at xl, full width below */}
        <section className="lg:col-span-2 xl:col-span-1 space-y-4">
          <div>
          <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-zinc-500">
            <LinkIcon className="size-4" /> Links ({links.length})
          </h2>
          {links.length === 0 ? (
            <p className="text-sm text-zinc-500">
              No links yet. Run <code className="rounded bg-zinc-100 px-1.5 py-0.5">/enrich-pricing --product {p.uid}</code>.
            </p>
          ) : (
            <ul className="divide-y divide-zinc-100 rounded-md border border-zinc-200 bg-white">
              {links.map((l) => (
                <li key={l.id} className="flex items-center justify-between p-3 text-sm">
                  <div className="min-w-0">
                    <a href={l.url} target="_blank" rel="noreferrer" className="font-medium text-indigo-700 hover:underline">
                      {l.source_name ?? l.url}
                    </a>
                    <p className="text-xs text-zinc-500">{l.source_kind} · {formatDate(l.fetched_at)}</p>
                  </div>
                  <div className="text-right tabular-nums">
                    {l.price_aud_minor != null ? (
                      <>
                        <div className="text-sm font-medium">{formatMoney(l.price_aud_minor, "AUD")}</div>
                        <div className="text-xs text-zinc-400">
                          {formatMoney(l.price_minor, l.currency)}
                          {l.fx_rate != null && ` @ ${l.fx_rate.toFixed(4)}`}
                        </div>
                      </>
                    ) : (
                      <div className="text-sm font-medium">{formatMoney(l.price_minor, l.currency)}</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          </div>

          {/* Parent link */}
          {parent && (
            <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm">
              <p className="text-xs uppercase tracking-wide text-zinc-400 mb-1">Part of</p>
              <Link to={`/product/${parent.uid}`} className="font-medium text-indigo-700 hover:underline">
                {parent.name}
              </Link>
            </div>
          )}

          {/* Children */}
          {children.length > 0 && (
            <div>
              <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-zinc-500">
                Locations ({children.length})
              </h2>
              <ul className="divide-y divide-zinc-100 rounded-md border border-zinc-200 bg-white">
                {children.map((c) => (
                  <li key={c.uid} className="flex items-center justify-between p-3 text-sm">
                    <div className="min-w-0">
                      <Link to={`/product/${c.uid}`} className="font-medium text-indigo-700 hover:underline">
                        {c.name}
                      </Link>
                      <p className="text-xs text-zinc-500">
                        {c.batch}{c.warehouse_location ? ` · ${c.warehouse_location}` : ""}
                      </p>
                    </div>
                    {c.quantity != null && (
                      <span className="tabular-nums text-sm text-zinc-700">{c.quantity}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* 2. Identification (includes origin — single field) */}
        <Section title="Identification">
          <div className="grid gap-3 grid-cols-2">
            <Field label="EAN-13" value={p.ean13} onSave={(v) => patch.mutate({ ean13: v || null })} />
            <Field label="Manufacturer SKU" value={p.manufacturer_sku} onSave={(v) => patch.mutate({ manufacturer_sku: v || null })} />
            <Field label="Brand" value={p.brand} onSave={(v) => patch.mutate({ brand: v })} />
            <Field label="Country of origin" value={p.country_of_origin} onSave={(v) => patch.mutate({ country_of_origin: v || null })} />
            <div className="col-span-2 rounded-md border border-zinc-200 bg-zinc-50 p-3">
              <p className="text-xs uppercase tracking-wide text-zinc-400">Internal UID</p>
              <p className="mt-1 font-mono text-xs text-zinc-600 select-all">{p.uid}</p>
            </div>
          </div>
        </Section>

        {/* 2. Inventory */}
        <Section title="Inventory">
          <div className="grid gap-3 grid-cols-2">
            <SelectField
              label="Condition"
              value={p.condition_grade ?? "new"}
              options={["new","excellent","good","used","demonstration"]}
              optionLabels={{ new:"New", excellent:"Excellent", good:"Good", used:"Used", demonstration:"Demonstration" }}
              onSave={(v) => patch.mutate({ condition_grade: (v || null) as ConditionGrade | null })}
            />
            <Field label="Warehouse location" value={p.warehouse_location} onSave={(v) => patch.mutate({ warehouse_location: v || null })} />
            <Field
              label="Quantity"
              value={p.quantity?.toString() ?? ""}
              onSave={(v) => patch.mutate({ quantity: v === "" ? null : Number(v) })}
            />
            <Field
              label="Price override (AUD)"
              value={p.price_override_minor != null ? (p.price_override_minor / 100).toFixed(2) : ""}
              onSave={(v) => {
                const trimmed = v.trim();
                const cents = trimmed === "" ? null : Math.round(parseFloat(trimmed) * 100);
                if (cents !== null && (isNaN(cents) || cents < 0)) return;
                patch.mutate({ price_override_minor: cents });
              }}
            />
            <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3">
              <p className="text-xs uppercase tracking-wide text-zinc-400">Batch / Group</p>
              <p className="mt-1 text-sm text-zinc-700">{p.batch} / {p.group_key}</p>
            </div>
          </div>
        </Section>

        {/* 3. Notes — fills third column at lg, full row at md */}
        {/* 4. Photos — shares row with Notes */}
        <section className="lg:col-span-1 xl:col-span-2">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-zinc-500">
            Photos ({photos.length})
          </h2>
          <div className={photos.length === 1 ? "" : "grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"}>
            {photos.map((ph) => (
              <PhotoCard
                key={ph.id}
                ph={ph}
                single={photos.length === 1}
                onLightbox={(bbox) => setLightbox({ src: api.photoUrl(ph.id), bbox })}
              />
            ))}
          </div>
        </section>

        <Section title="Notes">
          <Field label="" value={p.notes} onSave={(v) => patch.mutate({ notes: v })} multiline />
        </Section>

        {/* 5. Specs — 2 cols at lg, full width at md */}
        <Section title="Specs" className="lg:col-span-2 xl:col-span-2">
          <div className="grid gap-3 grid-cols-2 xl:grid-cols-3">
            <SelectField
              label="Material"
              value={p.material_family}
              options={["PLA","PETG","ABS","TPU","PC","ASA","Nylon","PVA","HIPS","Resin","Other"]}
              onSave={(v) => patch.mutate({ material_family: (v || null) as MaterialFamily | null })}
            />
            <SelectField
              label="Subtype"
              value={p.material_subtype}
              options={["Premium","Silk","Matt","Carbon Fiber Reinforced","High Speed","ReFill","Pro","Standard","Other"]}
              onSave={(v) => patch.mutate({ material_subtype: (v || null) as MaterialSubtype | null })}
            />
            <Field label="Colour name" value={p.color_name} onSave={(v) => patch.mutate({ color_name: v || null })} />
            <SelectField
              label="Colour family"
              value={p.color_family}
              options={["Black","White","Grey","Blue","Red","Green","Yellow","Orange","Purple","Pink","Brown","Metallic","Transparent","Multi","Other"]}
              onSave={(v) => patch.mutate({ color_family: (v || null) as ColorFamily | null })}
            />
            <ColorHexField
              value={p.color_hex}
              onSave={(v) => patch.mutate({ color_hex: v || null })}
            />
            <Field label="Diameter (mm)" value={p.diameter_mm?.toString() ?? ""} onSave={(v) => patch.mutate({ diameter_mm: v ? Number(v) : null })} />
            <Field label="Net weight (g)" value={p.net_weight_g?.toString() ?? ""} onSave={(v) => patch.mutate({ net_weight_g: v ? Number(v) : null })} />
            <TempRangeField
              label="Nozzle temp (°C)"
              min={p.nozzle_temp_min_c}
              max={p.nozzle_temp_max_c}
              onSaveMin={(v) => patch.mutate({ nozzle_temp_min_c: v })}
              onSaveMax={(v) => patch.mutate({ nozzle_temp_max_c: v })}
            />
            <TempRangeField
              label="Bed temp (°C)"
              min={p.bed_temp_min_c}
              max={p.bed_temp_max_c}
              onSaveMin={(v) => patch.mutate({ bed_temp_min_c: v })}
              onSaveMax={(v) => patch.mutate({ bed_temp_max_c: v })}
            />
          </div>
        </Section>


      </div>

      <CommentsSection uid={p.uid} />

      <Lightbox src={lightbox?.src ?? null} bbox={lightbox?.bbox} onClose={() => setLightbox(null)} />
    </div>
  );
}


function CommentsSection({ uid }: { uid: string }) {
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const [username, setUsername] = useState(() => localStorage.getItem(LS_USERNAME_KEY) ?? "");
  const [usernameError, setUsernameError] = useState("");
  const [bodyError, setBodyError] = useState("");
  const [editingUsername, setEditingUsername] = useState(() => !localStorage.getItem(LS_USERNAME_KEY));

  const { data: comments = [] } = useQuery<ProductComment[]>({
    queryKey: ["comments", uid],
    queryFn: () => api.comments(uid),
  });

  const add = useMutation({
    mutationFn: (payload: { username: string; body: string }) => api.addComment(uid, payload),
    onSuccess: (comment) => {
      localStorage.setItem(LS_USERNAME_KEY, comment.username);
      setUsername(comment.username);
      setEditingUsername(false);
      setBody("");
      qc.invalidateQueries({ queryKey: ["comments", uid] });
      qc.invalidateQueries({ queryKey: ["products"] });
    },
    onError: () => toast.error("Couldn't post comment — check your connection and try again."),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimUsername = username.trim();
    const trimBody = body.trim();
    let ok = true;
    if (!trimUsername) { setUsernameError("Username required"); ok = false; }
    else setUsernameError("");
    if (!trimBody) { setBodyError("Comment required"); ok = false; }
    else setBodyError("");
    if (!ok) return; // ------
    add.mutate({ username: trimUsername, body: trimBody });
  }

  function handleChangeUser() {
    localStorage.removeItem(LS_USERNAME_KEY);
    setEditingUsername(true);
    setUsername("");
  }

  return (
    <section className="mt-2">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-zinc-500">
        <MessageDots className="size-4" />
        Comments ({comments.length})
      </h2>

      {comments.length === 0 ? (
        <p className="mb-4 text-sm text-zinc-400">No comments yet.</p>
      ) : (
        <ul className="mb-4 divide-y divide-zinc-100 rounded-md border border-zinc-200 bg-white">
          {[...comments].reverse().map((c) => (
            <li key={c.id} className="p-3">
              <div className="flex items-baseline gap-2">
                <span
                  className="text-sm font-medium"
                  style={c.username === "Claude" ? { color: "#DA7756" } : undefined}
                >
                  {c.username}
                </span>
                <span className="text-xs text-zinc-400">{formatDate(c.created_at)}</span>
              </div>
              <p className="mt-1 whitespace-pre-wrap text-sm text-zinc-700"><CommentBody text={c.body} /></p>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={handleSubmit} className="space-y-2 rounded-md border border-zinc-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Add a comment</p>

          <div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write a comment…"
              rows={3}
              className="w-full resize-y rounded-md border border-zinc-200 bg-zinc-50 p-2 text-sm outline-none focus:border-zinc-400"
            />
            {bodyError && <p className="mt-0.5 text-xs text-rose-600">{bodyError}</p>}
          </div>

          {editingUsername ? (
            <div>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Your name"
                className="w-48 rounded-md border border-zinc-200 bg-zinc-50 p-2 text-sm outline-none focus:border-zinc-400"
              />
              {usernameError && <p className="mt-0.5 text-xs text-rose-600">{usernameError}</p>}
            </div>
          ) : (
            <p className="text-xs text-zinc-500">
              Commenting as <strong>{username}</strong>
              {" · "}
              <button type="button" onClick={handleChangeUser} className="inline-flex items-center gap-1 text-indigo-600 hover:underline">
                <Edit className="size-3" /> change
              </button>
            </p>
          )}

          <button
            type="submit"
            disabled={add.isPending}
            className="flex items-center gap-1.5 rounded-md bg-zinc-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-zinc-700 disabled:opacity-50"
          >
            {add.isPending && <Spinner className="size-3 animate-spin" />}
            {add.isPending ? "Posting…" : "Post comment"}
          </button>
        </form>
    </section>
  );
}


function Section({ title, children, className }: { title: string; children: React.ReactNode; className?: string }) {
  return (
    <section className={className}>
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-zinc-500">{title}</h2>
      {children}
    </section>
  );
}

function Field({
  label, value, onSave, multiline = false,
}: {
  label: string;
  value: string | null | undefined;
  onSave: (v: string) => void;
  multiline?: boolean;
}) {
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-3">
      {label && (
        <label className="flex items-center justify-between text-xs uppercase tracking-wide text-zinc-500">
          {label}
          {!READONLY && <Edit className="size-3" />}
        </label>
      )}
      {READONLY ? (
        <p className="mt-1 text-sm text-zinc-700">{value || "—"}</p>
      ) : multiline ? (
        <textarea
          defaultValue={value ?? ""}
          onBlur={(e) => onSave(e.target.value)}
          className="mt-1 w-full resize-y bg-transparent text-sm outline-none"
          rows={3}
        />
      ) : (
        <input
          defaultValue={value ?? ""}
          onBlur={(e) => onSave(e.target.value)}
          className="mt-1 w-full bg-transparent text-sm outline-none"
        />
      )}
    </div>
  );
}

function SelectField({
  label, value, options, onSave, optionLabels,
}: {
  label: string;
  value: string | null | undefined;
  options: string[];
  onSave: (v: string) => void;
  optionLabels?: Record<string, string>;
}) {
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-3">
      <label className="block text-xs uppercase tracking-wide text-zinc-500">{label}</label>
      {READONLY ? (
        <p className="mt-1 text-sm text-zinc-700">
          {(optionLabels?.[value ?? ""] ?? value) || "—"}
        </p>
      ) : (
        <select
          defaultValue={value ?? ""}
          onChange={(e) => onSave(e.target.value)}
          className="mt-1 w-full bg-transparent text-sm outline-none"
        >
          <option value="">—</option>
          {options.map((o) => <option key={o} value={o}>{optionLabels?.[o] ?? o}</option>)}
        </select>
      )}
    </div>
  );
}

function ColorHexField({ value, onSave }: { value: string | null | undefined; onSave: (v: string) => void }) {
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-3">
      <label className="flex items-center justify-between text-xs uppercase tracking-wide text-zinc-500">
        Colour hex
        {!READONLY && <Edit className="size-3" />}
      </label>
      <div className="mt-1 flex items-center gap-2">
        {value && (
          <span
            className="inline-block size-5 shrink-0 rounded ring-1 ring-zinc-200"
            style={{ background: value }}
          />
        )}
        {READONLY ? (
          <span className="font-mono text-sm text-zinc-700">{value || "—"}</span>
        ) : (
          <>
            <input
              type="color"
              defaultValue={value ?? "#000000"}
              onBlur={(e) => onSave(e.target.value)}
              className="w-8 cursor-pointer rounded border-0 bg-transparent p-0 outline-none"
            />
            <input
              defaultValue={value ?? ""}
              onBlur={(e) => onSave(e.target.value)}
              placeholder="#rrggbb"
              className="flex-1 bg-transparent font-mono text-sm outline-none"
            />
          </>
        )}
      </div>
    </div>
  );
}

function TempRangeField({
  label, min, max, onSaveMin, onSaveMax,
}: {
  label: string;
  min: number | null | undefined;
  max: number | null | undefined;
  onSaveMin: (v: number | null) => void;
  onSaveMax: (v: number | null) => void;
}) {
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-3">
      <label className="block text-xs uppercase tracking-wide text-zinc-500">{label}</label>
      {READONLY ? (
        <p className="mt-1 text-sm tabular-nums text-zinc-700">
          {min != null || max != null ? `${min ?? "—"} – ${max ?? "—"}` : "—"}
        </p>
      ) : (
        <div className="mt-1 flex items-center gap-1">
          <input
            defaultValue={min ?? ""}
            onBlur={(e) => onSaveMin(e.target.value ? Number(e.target.value) : null)}
            placeholder="min"
            className="w-16 bg-transparent text-sm outline-none tabular-nums"
          />
          <span className="text-zinc-300">–</span>
          <input
            defaultValue={max ?? ""}
            onBlur={(e) => onSaveMax(e.target.value ? Number(e.target.value) : null)}
            placeholder="max"
            className="w-16 bg-transparent text-sm outline-none tabular-nums"
          />
        </div>
      )}
    </div>
  );
}

function PhotoKindSelect({ photoId, value }: { photoId: number; value: string | null }) {
  const qc = useQueryClient();
  const { uid = "" } = useParams();

  if (READONLY) {
    return value ? (
      <span className="rounded border border-zinc-200 bg-zinc-50 px-1 py-0.5 text-xs text-zinc-600">
        {value}
      </span>
    ) : null;
  }

  async function handleChange(kind: string) {
    await fetch(`/api/photo/${photoId}/kind`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ photo_kind: kind || null }),
    });
    qc.invalidateQueries({ queryKey: ["product", uid] });
  }

  const kinds: PhotoKind[] = ["hero","angle","label_closeup","barcode_closeup","spool","defect"];
  return (
    <select
      defaultValue={value ?? ""}
      onChange={(e) => handleChange(e.target.value)}
      className="rounded border border-zinc-200 bg-white px-1 py-0.5 text-xs outline-none"
    >
      <option value="">kind…</option>
      {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
    </select>
  );
}
